/// <amd-module name="TFS/Token/PatTokenPage/Components/Panels/Panel.AddOrEdit" />
import FutureDatePicker from 'Components/Common/FutureDatePicker';
import ScopeGroupings from "Components/Common/ScopeGrouping";
import { TokenMode } from "Components/Common/FutureDatePicker";
import { basePanelProps } from 'Components/Panels/Panel.Base';
import { AllOrganizations, AppToken, CurrentOrganization, ScopeSelectionCustom, ScopeSelectionFull } from 'Constants/TokenConstants';
import { IPatPageContextValue, PatPageConsumer } from 'Context/PatPageContext';
import { SessionToken } from 'Contracts/Token';
import { PatTokenPageData, ScopeData, ScopeGrouping } from 'Generated/Contracts';
import { TokenConfig } from 'Models/TokenConfig';
import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";
import { FontSizes, getTheme, ITheme, mergeStyleSets } from "OfficeFabric/Styling";
import * as React from 'react';
import * as Resources from "TFS/Token/PatTokenPage/Resources";
import ScopeUtils from 'Utils/ScopeUtils';
import { memoize } from "VSS/Core/Util/Memoize";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from 'VSS/Platform/Layout';
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { CustomPanel, ICustomPanelProps, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { Token } from '../../Models/Token';
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IOrganizationAadDelayLoadSettingsData, IAadTenantPolicy } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IAddOrEditTokenDialogProps extends Pick<ICustomPanelProps, "onDismiss"> {
    token?: Token,
    accounts?: { accountId: string, accountName: string }[],
    accountIdToNameMap?: { [accountId: string]: string };
    addToken: (newToken: Token) => Promise<void>,
    updateToken: (token: Token) => Promise<void>
}

export interface IAddOrEditTokenDialogInternalProps extends IAddOrEditTokenDialogProps, ICustomPanelProps {
    tokenConfig: TokenConfig;
}

export interface IAddOrEditTokenDialogInternalState {
    scopeExpansionStatus: ScopeExpansionStatus,
    selectedTokenName: string,
    selectedAccount: string,
    selectedExpirationDate: Date,
    startingExpirationKey: string,
    selectedScopeRadioButtonValue: string,
    errorMessage?: string,
    isMakingApiCall: boolean,
    createdToken?: SessionToken,
    allowAllOrganizations: boolean,
    allowFullAccess: boolean,
    maxExpirationDaysNumber: number,
    expirationDaysDropdownOptions: any,
    bannerMessage: string,
    tokenLifespanPolicyData?: IAadTenantPolicy
}

export const enum ScopeExpansionStatus {
    NotExpanded,
    ExpandedPartialy,
    ExpandedAll
}

export class AddOrEditTokenPanelInternal extends VssComponent<IAddOrEditTokenDialogInternalProps, IAddOrEditTokenDialogInternalState> {
    constructor(props: IAddOrEditTokenDialogInternalProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this.initializedState;
        this.setInitializedGroupIdToSelectedScopeIdMap();
    }

    private groupIdToSelectedScopeIdMap: { [groupName: string]: ScopeData[] } = {};
    private static OLD_DEFAULT_MAX_TOKEN_LIFESPAN_IN_DAYS = 364;
    private static readonly DEFAULT_DATE_DROPDOWN_OPTIONS = 
    [
        { key: "30", daysAhead: 30 },
        { key: "60", daysAhead: 60 },
        { key: "90", daysAhead: 90 },
    ];

    private static readonly CUSTOM_EXPIRATION_KEY = "custom";
    private tokenLifespanPolicyToggleEnabled = false;

    @memoize
    private static getStyles(theme: ITheme) {
        const { palette } = theme;
        return mergeStyleSets({
            panelContentContainer: {
                display: "flex",
                flexDirection: "column",
                height: "inherit",
                width: "100%",
            },
            scopeGroupingSpaceAbove: {
                paddingTop: "20px",
            },
            scopeGroupingListContainer: {
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                minHeight: "100px",
                borderTop: `1px solid ${palette.neutralSecondary}`,
            },
            scopeGroupingContainer: {
                selectors: {
                    " > * + *": {
                        borderTop: `1px solid ${palette.neutralSecondary}`,
                    },
                },
            },
            nameField: {
                flexShrink: 0,
            },
            futureDatePicker: {
                flexShrink: 0,
            },
            scopesTitle: {
                fontSize: FontSizes.large,
                flexShrink: 0,
                paddingTop: "20px"
            },
            scopeRadioButtons: {
                display: "flex",
                flexShrink: 0,
                flexDirection: "row"
            },
            scopeLinksContainer: {
                flexShrink: 0,
                paddingTop: "12px"
            },
            scopeCount: {
                paddingLeft: "4px",
                color: palette.neutralSecondary
            },
            footerButtons: {
                paddingRight: "16px",
                selectors: {
                    " > * + *": {
                        marginLeft: "8px",
                    },
                },
            },
            errorMessage: {
                flexShrink: 0,
                paddingTop: "20px"
            },
        })
    }

    public render() {
        const { token } = this.props;
        const styles = this.getStyles();
        const { tokenPageData } = this.props.tokenConfig;
        const {
            selectedTokenName,
            selectedExpirationDate,
            startingExpirationKey,
            selectedScopeRadioButtonValue,
            bannerMessage
        } = this.state;

        return (
            <CustomPanel
                {...basePanelProps}
                {...this.props}
                size={ContentSize.Large}
                defaultActiveElement={styles.nameField}
            >
                <PanelHeader onDismiss={this.props.onDismiss} titleProps={{ text: token ? Resources.EditPatToken : Resources.NewTokenPanelDescription }}>
                </PanelHeader>
                { bannerMessage && 
                    <MessageBar severity={MessageBarSeverity.Warning}>
                        <span>{bannerMessage}{" "}
                            <Link
                                className="help-link"
                                href={Resources.PatPoliciesLearnMoreLink}
                                target="_blank"
                            >
                                { Resources.LearnMore }
                            </Link>
                        </span>
                    </MessageBar>
                }
                <PanelContent>
                    <div className={styles.panelContentContainer}>
                        {/* Token Name */}
                        <FormItem className={styles.nameField} label={this._renderRequiredFieldLabel(Resources.Name)}>
                            <TextField
                                ariaLabel={Resources.Name}
                                value={selectedTokenName}
                                required={true}                                
                                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string) => this.setState({ selectedTokenName: name })}
                            />
                        </FormItem>
                        {this.renderOrgPickerDropdown()}

                        {/* Expiration Values Dropdown */}
                        <div className={styles.futureDatePicker}>
                            <FutureDatePicker
                                date={selectedExpirationDate}
                                dropDownOptions={this.state.expirationDaysDropdownOptions}
                                defaultOptionKey={startingExpirationKey}
                                onDateChange={(date: Date) => this.setState({ selectedExpirationDate: date })}
                                maxDaysNumber={this.state.maxExpirationDaysNumber}
                                tokenMode={token ? TokenMode.Edit : TokenMode.Create}
                                validFrom={token ? token.validFrom : new Date()}
                                tokenLifespanPolicyToggleEnabled={this.tokenLifespanPolicyToggleEnabled}
                                pageContext={this.context.pageContext}
                            />
                        </div>


                        {/* Scopes */}
                        <div className={styles.scopesTitle}>{Resources.Scopes}</div>
                        <div>{Resources.NewTokenScopeDescription}</div>
                            <RadioButtonGroup
                                className={styles.scopeRadioButtons}
                                text={Resources.Scopes}
                                selectedButtonId={selectedScopeRadioButtonValue}
                                onSelect={(selectedButtonId: string) => this.updateScopeRadioButtonState(selectedButtonId)}
                            >
                                <RadioButton id={ScopeSelectionFull} text={Resources.FullAccess} disabled={!this.state.allowFullAccess} />
                                <RadioButton id={ScopeSelectionCustom} text={Resources.CustomDefined} />
                            </RadioButtonGroup>
                        {/* Scope Groupings */}
                        {this.renderScopes(tokenPageData)}

                        {/* Create Token Error */}
                        {this.renderCreateTokenError()}
                    </div>
                </PanelContent>
                <PanelFooter>{this.renderAddFooterContent()}</PanelFooter>
            </CustomPanel>
        )
    }

    private _renderRequiredFieldLabel(label: string) {
        return <span className="required-field">{label}</span>;
    }

    private renderOrgPickerDropdown() {
        const { selectedAccount } = this.state;
        const { accountIdToNameMap } = this.props;
        const availableOptions: { key: string, text: string }[] = [
            {
                key: this.props.tokenConfig.hostPageData!.hostId || CurrentOrganization,
                text: this.props.tokenConfig.hostPageData!.hostName || Resources.CurrentOrganization
            },
            {
                key: AllOrganizations,
                text: Resources.AllAccessibleOrganizations
            }];

        const { token } = this.props;
        const restrictAllOrganizationsPersonalAccessToken = token ? isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.PATs.RestrictAllOrganizationsPersonalAccessToken", false) : false;

        if (!availableOptions.find((option) => option.key === selectedAccount)) {
            return <FormItem label={Resources.Organization}>
                <TextField
                    value={
                        accountIdToNameMap && accountIdToNameMap[selectedAccount]
                            ? accountIdToNameMap[selectedAccount]
                            : Resources.UnknownOrganization
                    }
                    disabled={true}
                    required={true}
                />
            </FormItem>
        }
        return <Dropdown
            ariaLabel={Resources.Organization}
            label={Resources.Organization}
            selectedKey={selectedAccount}
            onChanged={(option: IDropdownOption) => this.setState({ selectedAccount: option.key.toString() })}
            options={availableOptions}
            disabled={!this.state.allowAllOrganizations || restrictAllOrganizationsPersonalAccessToken}
        />
    }

    private get initializedState(): IAddOrEditTokenDialogInternalState {
        const { token, accounts } = this.props;
        const { tokenPageData } = this.props.tokenConfig;
        const { userCanCreateFullScopedTokens, userCanCreateGlobalTokens, userCanCreateUnlimitedLifespanTokens } = tokenPageData;
        const userTokenLifespanLimitInDays = userCanCreateUnlimitedLifespanTokens ? AddOrEditTokenPanelInternal.OLD_DEFAULT_MAX_TOKEN_LIFESPAN_IN_DAYS : tokenPageData.userTokenLifespanLimitInDays;

        let expirationDropdownOptions = [];
        let bannerMessage = "";

        let i = 0;
        const daysPassedForToken = this.getPassedDaysForToken(token);
        while (i < AddOrEditTokenPanelInternal.DEFAULT_DATE_DROPDOWN_OPTIONS.length &&
            AddOrEditTokenPanelInternal.DEFAULT_DATE_DROPDOWN_OPTIONS[i].daysAhead <= (userTokenLifespanLimitInDays - daysPassedForToken)) {
            expirationDropdownOptions.push(AddOrEditTokenPanelInternal.DEFAULT_DATE_DROPDOWN_OPTIONS[i++]);
        }

        const defaultTokenLifespan = expirationDropdownOptions.length ? expirationDropdownOptions[0].daysAhead : userTokenLifespanLimitInDays;
        const defaultExpirationDate = new Date();
        defaultExpirationDate.setDate(new Date().getDate() + defaultTokenLifespan);
        const maxAllowedExpirationDate = new Date();
        maxAllowedExpirationDate.setDate(new Date().getDate() + userTokenLifespanLimitInDays);

        if (!userCanCreateFullScopedTokens ||  !userCanCreateGlobalTokens || !userCanCreateUnlimitedLifespanTokens) {
            let fullScopedGlobalString = 
                !userCanCreateFullScopedTokens ?  Resources.FullScopedPat :  "";
            fullScopedGlobalString = 
                !userCanCreateGlobalTokens ? 
                    (fullScopedGlobalString ?  format(Resources.FullScopedGlobalPatFormat, fullScopedGlobalString, Resources.GlobalPat) : Resources.GlobalPat) :
                    fullScopedGlobalString;
            const maxPatMaxLifespanFormatString = 
                !userCanCreateUnlimitedLifespanTokens ? format(Resources.PatMaxLifespanFormat, userTokenLifespanLimitInDays) : "";

            bannerMessage = format(Resources.PatPoliciesChangedWarningFormat, fullScopedGlobalString, maxPatMaxLifespanFormatString);
        }
        
        let defaultState: IAddOrEditTokenDialogInternalState = {
            scopeExpansionStatus: ScopeExpansionStatus.ExpandedPartialy,
            selectedTokenName: "",
            selectedAccount: this.props.tokenConfig.hostPageData!.hostId || CurrentOrganization,
            selectedExpirationDate: defaultExpirationDate,
            selectedScopeRadioButtonValue: ScopeSelectionCustom,
            errorMessage: undefined,
            isMakingApiCall: false,
            allowAllOrganizations: userCanCreateGlobalTokens,
            allowFullAccess: userCanCreateFullScopedTokens,
            startingExpirationKey: expirationDropdownOptions.length ? expirationDropdownOptions[0].key : AddOrEditTokenPanelInternal.CUSTOM_EXPIRATION_KEY,
            maxExpirationDaysNumber: userTokenLifespanLimitInDays,
            expirationDaysDropdownOptions: expirationDropdownOptions,
            bannerMessage: bannerMessage
        };
        
        if (token) {
            let selectedAccountHost = null;
            if (isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.PATs.RestrictPersonalAccessTokenOrganizationChange", false)) {
                selectedAccountHost = this.getSelectedAccountHost(token, accounts);
            } else {
                selectedAccountHost = userCanCreateGlobalTokens ? this.getSelectedAccountHost(token, accounts) : (this.props.tokenConfig.hostPageData!.hostId || CurrentOrganization);
            }

            defaultState = {
                ...defaultState,
                selectedTokenName: token.displayName,
                selectedAccount: selectedAccountHost,
                // if the user can create full scoped tokens, expand or not depending on the scope of the token to update, otherwise show expanded view
                scopeExpansionStatus:
                    (userCanCreateFullScopedTokens && token.scope === AppToken) ?
                        ScopeExpansionStatus.NotExpanded :
                        ScopeExpansionStatus.ExpandedAll,
                // if token expiration date is out of allowed values range, set it to maximum valid value
                selectedExpirationDate:
                    userCanCreateUnlimitedLifespanTokens ?
                        token.validTo :
                        (token.validTo > maxAllowedExpirationDate ? maxAllowedExpirationDate : token.validTo),
                startingExpirationKey: AddOrEditTokenPanelInternal.CUSTOM_EXPIRATION_KEY,
                // if the user can create full scoped tokens, show the token scope to update, otherwise force select custom
                selectedScopeRadioButtonValue:
                    (userCanCreateFullScopedTokens && token.scope === AppToken) ? ScopeSelectionFull : ScopeSelectionCustom,
            };
        }

        return defaultState;
    }

    private getSelectedAccountHost = (token: Token, accounts?: { accountId: string, accountName: string }[]): string => {
        return token.targetAccounts && accounts && token.targetAccounts.length > 0 ? token.targetAccounts[0] : AllOrganizations;
    }

    private getPassedDaysForToken = (token?: Token): number => {
        if (isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.TenantPolicy.UseFixedPersonalAccessTokenExpirationDate", false) &&
            isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.TenantPolicy.Admin.PATs.RestrictTokenLifespanPolicy", false) &&
            this.isTokenLifespanPolicyToggleEnabled()) {
            if (token === undefined)
                return 0;
            const today = new Date();
            const tokenValidFromTime = token.validFrom.getTime();
            const offsetMs = Math.abs(tokenValidFromTime - today.getTime());
            return offsetMs / (1000 * 3600 * 24);
        }
        return 0;
    };

    private setInitializedGroupIdToSelectedScopeIdMap = () => {
        const { token, tokenConfig } = this.props;
        this.groupIdToSelectedScopeIdMap = {};
        if (token) {
            // create selected token mapping
            const selectedTokenKeys = token.scope.split(' ');
            const selectedTokenKeyMap: { [tokenKey: string]: boolean } = {};
            selectedTokenKeys.forEach((key) => {
                selectedTokenKeyMap[key] = true;
            });

            // go through all of our groups, and assemble selected scope lists
            const { scopes } = tokenConfig.tokenPageData;
            const groupIds = Object.keys(scopes);
            groupIds.forEach((groupId) => {
                const group = scopes[groupId];
                let selectedScopes: ScopeData[] = [];
                group.scopes.forEach((scope) => {
                    if (selectedTokenKeyMap[scope.key]) {
                        selectedScopes = selectedScopes.concat(scope);
                    }
                });
                this.groupIdToSelectedScopeIdMap[groupId] = selectedScopes;
            });

        }
    }

    // updates list of selected scopes
    private onCheckboxChange = (scope: ScopeData, group: ScopeGrouping, isChecked?: boolean): void => {
        let currentScopes = this.groupIdToSelectedScopeIdMap[group.key]
            ? [...this.groupIdToSelectedScopeIdMap[group.key]]
            : [];

        // remove from groupIdToSelectedScopeIdMap
        if (!isChecked) {
            currentScopes = currentScopes.filter((data) => data.key !== scope.key);
        } else {
            // filter out scopes where they are a child of me. then concat me to the list
            currentScopes = currentScopes.filter(child => !ScopeUtils.isParentOf(scope /* parent */, child, currentScopes)); // filter out current scopes where scope to add is a parent of currently
            currentScopes = currentScopes.concat(scope);
        }
        this.groupIdToSelectedScopeIdMap[group.key] = currentScopes;
        this.forceUpdate();
    }

    private updateScopeRadioButtonState = (optionKey: string): void => {
        optionKey === ScopeSelectionFull ? this.setState({ scopeExpansionStatus: ScopeExpansionStatus.NotExpanded }) : this.setState({ scopeExpansionStatus: ScopeExpansionStatus.ExpandedPartialy });
        this.setState({ selectedScopeRadioButtonValue: optionKey })
    }

    // conditionally renders scopes based off of current scopeExpansionStatus
    // Has three options: 
    // 1) Show nothing when selected scope is full 
    // 2) 'Show scopes' link 
    // 3) Renders selected number of scope groups + 'Show all scopes' link
    // 4) Renders all scope groups
    private renderScopes = (pageData: PatTokenPageData): JSX.Element | null => {
        if (this.state.selectedScopeRadioButtonValue === ScopeSelectionFull) {
            return null;
        }
        const styles = this.getStyles();
        const scopeExpansionStatus = this.state.scopeExpansionStatus;
        const groupIds = Object.keys(pageData.scopes);


        // display link to render selected number of scopes
        if (scopeExpansionStatus === ScopeExpansionStatus.NotExpanded) {
            return (
                <div className={styles.scopeLinksContainer}>
                    <Link onClick={() => this.setState({ scopeExpansionStatus: ScopeExpansionStatus.ExpandedPartialy, selectedScopeRadioButtonValue: ScopeSelectionCustom })}>{Resources.ShowScopes}</Link>
                    <span className={styles.scopeCount}>({groupIds.length})</span>
                </div>
            )
        } else {
            var isPartial = scopeExpansionStatus === ScopeExpansionStatus.ExpandedPartialy;
            return (
                <>
                    <div className={styles.scopeGroupingSpaceAbove} />
                    <div className={styles.scopeGroupingListContainer}>
                        {isPartial
                            ? this.renderSuggestedScopeGroupings(pageData)
                            : this.renderAllScopeGroupings(pageData)}
                    </div>
                    <div className={styles.scopeLinksContainer}>
                        <Link onClick={this.onChangeShowResultLinkClicked}>{isPartial
                            ? Resources.ShowAllScopes
                            : Resources.ShowLessScopes}
                        </Link>
                        {isPartial && <span className={styles.scopeCount}>({groupIds.length - pageData.coreScopeGroupings.length!} {Resources.More})</span>}
                    </div>
                </>
            )
        }
    }

    private renderSuggestedScopeGroupings = (pageData: PatTokenPageData): JSX.Element => {
        return this.renderFormattedScopeGroupings(pageData.coreScopeGroupings.map((groupId) => pageData.scopes[groupId]));
    }

    private renderAllScopeGroupings = (pageData: PatTokenPageData): JSX.Element => {
        const groupIds = Object.keys(pageData.scopes);
        const groups = groupIds.map((groupId) => pageData.scopes[groupId]).sort((group1, group2) => group1.name.localeCompare(group2.name, navigator.language));
        return this.renderFormattedScopeGroupings(groups);
    }

    private renderFormattedScopeGroupings = (groups: ScopeGrouping[]): JSX.Element => {
        const styles = this.getStyles();
        return (
            <div className={styles.scopeGroupingContainer}>
                {groups.map((group) => {
                    return (
                        <ScopeGroupings
                            key={group.name}
                            group={group}
                            selectedScopes={this.groupIdToSelectedScopeIdMap[group.key] || []}
                            onCheckboxChange={this.onCheckboxChange}
                        />
                    )
                })}
            </div>
        )
    }

    private renderAddFooterContent = (): JSX.Element => {
        const { token } = this.props;
        const { isMakingApiCall } = this.state;
        const styles = this.getStyles();
        return (
            <div className={styles.footerButtons}>
                <Button
                    primary={true}
                    disabled={this.createButtonDisabled}
                    onClick={this.createToken}
                >
                    {isMakingApiCall && <Spinner size={SpinnerSize.medium} />}
                    {token ? Resources.Save : Resources.Create}
                </Button>
                <Button
                    onClick={this.props.onDismiss}
                    disabled={isMakingApiCall}
                >
                    {Resources.Cancel}
                </Button>
            </div>
        )
    }

    private areScopesSelected = (): boolean => {
        const scopeMap = this.groupIdToSelectedScopeIdMap;
        for (let group in scopeMap) {
            if (scopeMap[group].length > 0) {
                return true;
            }
        }
        return false;
    }

    // renders message bar with error message
    private renderCreateTokenError = () => {
        const styles = this.getStyles();
        if (this.state.errorMessage) {
            return (
                <div className={styles.errorMessage}>
                    <MessageBar
                        severity={MessageBarSeverity.Error}>
                        {this.state.errorMessage}
                    </MessageBar>
                </div>
            )
        }
    }

    private onChangeShowResultLinkClicked = () => {
        this.setState({
            scopeExpansionStatus: this.state.scopeExpansionStatus === ScopeExpansionStatus.ExpandedPartialy
                ? ScopeExpansionStatus.ExpandedAll
                : ScopeExpansionStatus.ExpandedPartialy
        })
    }

    private get createButtonDisabled(): boolean {
        return !(this.state.selectedTokenName || !!this.props.token) ||
            this.state.isMakingApiCall ||
            (this.state.selectedScopeRadioButtonValue === ScopeSelectionCustom && !this.areScopesSelected());
    }

    private createToken = async () => {
        const { addToken, updateToken } = this.props;
        this.setState({ isMakingApiCall: true });
        let newToken = this.props.token || new Token();
        // name
        newToken.displayName = this.state.selectedTokenName;

        if (isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.PATs.RestrictPersonalAccessTokenOrganizationChange", false)) {
            // change targetAccounts only on token create
            if (!this.props.token) {
                newToken.targetAccounts = this.state.selectedAccount === AllOrganizations
                    ? []
                    : [this.state.selectedAccount];
            }
        } else {
            newToken.targetAccounts = this.state.selectedAccount === AllOrganizations
                ? []
                : [this.state.selectedAccount];
        }

        // expiration
        const currentDate = new Date();
        newToken.validFrom = currentDate;
        newToken.validTo = this.state.selectedExpirationDate;
        // scopes
        newToken.scope = this.getSelectedScopes(this.groupIdToSelectedScopeIdMap);

        // make rest API call
        try {
            if (this.props.token) {
                await updateToken(newToken);
            } else {
                await addToken(newToken);
            }
            if (this.props.onDismiss) {
                this.props.onDismiss();
            }
        } catch (ex) {
            this.setState({ errorMessage: ex, isMakingApiCall: false });
        }
    }

    // adds all selected scopes to a string to be used when creating a token
    private getSelectedScopes = (selectedScopeMap: { [groupName: string]: ScopeData[] }): string => {
        if (this.state.selectedScopeRadioButtonValue === ScopeSelectionFull) {
            return "app_token";
        }
        let scopeString = "";
        for (let group in selectedScopeMap) {
            if (selectedScopeMap[group].length > 0) {
                for (let scope of selectedScopeMap[group]) {
                    if (scopeString.length === 0) {
                        scopeString += scope.key;
                    } else {
                        scopeString += " " + scope.key;
                    }
                }
            }
        }
        if (scopeString.length === 0) {
            return "app_token";
        }
        return scopeString;
    }

    private getStyles() {
        const theme = getTheme();
        return AddOrEditTokenPanelInternal.getStyles(theme);
    }

    private isTokenLifespanPolicyToggleEnabled() {
        return this.tokenLifespanPolicyToggleEnabled;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Services.TenantPolicy.UseFixedPersonalAccessTokenExpirationDate", false)) {
            this._updateDelayLoad();
        }
    }

    private _updateDelayLoad = async () => {
        var contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var settingsData = await contributionService.getDataAsync<IOrganizationAadDelayLoadSettingsData>("ms.vss-admin-web.organization-admin-aad-delay-load-data-provider")!;
        if (settingsData) {
            if (settingsData.tokenLifespanPolicy) {
                this.tokenLifespanPolicyToggleEnabled = settingsData.tokenLifespanPolicy.isPolicyEnabled === true;
            } else {
                this.tokenLifespanPolicyToggleEnabled = false;
            }
        }
        this.state = this.initializedState;
        this.setState({
            tokenLifespanPolicyData: settingsData && settingsData.tokenLifespanPolicy ? settingsData.tokenLifespanPolicy : undefined
        });
    }
}

export const AddOrEditTokenPanel = (props: IAddOrEditTokenDialogProps) =>
    <PatPageConsumer>
        {(patPageContext: IPatPageContextValue) =>
            <AddOrEditTokenPanelInternal
                {...props}
                tokenConfig={TokenConfig.getInstance(patPageContext.pageContext)}
            />}
    </PatPageConsumer>;
;
